import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconStatusChange } from '@tabler/icons-react';
import { CrudMode, CrudSchemaField, type EnsoFormItem } from '@ohpen/enso-ui';

import { translatedValueEnum } from '@pmp/utils';
import { TabProps } from '../interfaces';

export const Status: React.FC<TabProps> = ({ form, mode, selectsOptions, defaultComplianceStatus }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;

  useEffect(() => {
    if (mode !== CrudMode.CREATE) return;
    if (defaultComplianceStatus) {
      const current = form?.getFieldValue(['status', 'complianceStatus']);
      if (current === undefined) {
        form?.setFieldValue(['status', 'complianceStatus'], defaultComplianceStatus);
      }
    }
    if (form?.getFieldValue(['status', 'onTheWatchlist']) === undefined) {
      form?.setFieldValue(['status', 'onTheWatchlist'], false);
    }
  }, [mode, defaultComplianceStatus, form]);

  const columns: EnsoFormItem[] = useMemo(
    () => [
      {
        title: t('firms.labels.compliance'),
        dataIndex: ['status', 'complianceStatus'],
        valueType: 'radio',
        readonly,
        formItemProps: {
          required: true,
        },
        fieldProps: {
          options: [
            { label: t('firms.labels.pending'), value: 'PENDING' },
            { label: t('firms.labels.failed'), value: 'FAILED' },
            { label: t('firms.labels.passed'), value: 'PASSED' },
            { label: t('firms.labels.reReviewRequired'), value: 'RE_REVIEW_REQUIRED' },
          ],
        },
        colProps: { span: 24 },
      },
      {
        title: t('firms.labels.watchlist'),
        dataIndex: ['status', 'onTheWatchlist'],
        valueType: 'radio',
        initialValue: mode === CrudMode.CREATE ? false : undefined,
        readonly,
        fieldProps: {
          options: [
            { label: t('enso.labels.yes'), value: true },
            { label: t('enso.labels.no'), value: false },
          ],
        },
        formItemProps: {
          required: true,
        },
        colProps: { span: 24 },
      },
      ...(mode === CrudMode.CREATE
        ? []
        : ([
            {
              title: t('firms.labels.complianceRecord'),
              valueType: 'group',
              colProps: { span: 24 },
              columns: [
                {
                  title: t('firms.labels.addedToPanel'),
                  dataIndex: ['status', 'addedToPanel'],
                  valueType: 'date',
                  readonly: true,
                  colProps: { span: 6 },
                },
                {
                  title: t('global.labels.addedBy'),
                  dataIndex: ['status', 'addedBy'],
                  valueType: 'text',
                  valueEnum: translatedValueEnum(t, 'global.systemActors'),
                  readonly: true,
                  colProps: { span: 6 },
                },
                {
                  title: t('global.labels.lastUpdate'),
                  dataIndex: ['status', 'lastUpdate'],
                  valueType: 'date',
                  readonly: true,
                  colProps: { span: 6 },
                },
                {
                  title: t('global.labels.updatedBy'),
                  dataIndex: ['status', 'updatedBy'],
                  valueType: 'text',
                  valueEnum: translatedValueEnum(t, 'global.systemActors'),
                  readonly: true,
                  colProps: { span: 6 },
                },
              ],
            },
            {
              valueType: 'group',
              colProps: { span: 24 },
              columns: [
                {
                  title: t('firms.labels.activationDate'),
                  dataIndex: ['status', 'activationDate'],
                  valueType: 'date',
                  readonly: true,
                  colProps: { span: 6 },
                },
                {
                  title: t('firms.labels.deactivationDate'),
                  dataIndex: ['status', 'deactivationDate'],
                  valueType: 'date',
                  readonly: true,
                  colProps: { span: 6 },
                },
              ],
            },
          ] satisfies EnsoFormItem[])),
    ],
    [readonly, mode, t],
  );

  const schema: EnsoFormItem = useMemo(
    () => ({
      valueType: 'group',
      variant: 'card',
      icon: <IconStatusChange />,
      title: t('firms.labels.status'),
      columns,
    }),
    [columns, t],
  );

  return <CrudSchemaField item={schema} form={form} readonly={readonly} />;
};
